import { PaginatedTable } from '@avanta/shared/components/PaginatedTable';
import { Text } from '@avanta/shared/components/Text';
import { Button } from '@avanta/shared/components/ui/Button';
import { ADMIN_ROLES } from '@avanta/shared/constants/admins';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { useDelayedLoading } from '@avanta/shared/hooks/useDelayedLoading';
import { t } from '@avanta/shared/i18n';
import { type ColumnDef } from '@tanstack/react-table';
import { UserCog } from 'lucide-react';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PageHeader } from 'components/PageHeader';
import { type AdminEnterprise } from 'types/enterprises';
import { AddAdministrator } from './components/AddAdministrator';
import { AdministratorActions } from './components/AdministratorActions';
import { useFetchAdmins } from './hooks/useFetchAdmins';

const getColumns = (): ColumnDef<AdminEnterprise>[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: t('AdministratorsPage.Table.name.label', { ns: LOCIZE_NS.INSURANCE }),
    enableSorting: true,
  },
  {
    id: 'surname',
    accessorKey: 'surname',
    header: t('AdministratorsPage.Table.surname.label', { ns: LOCIZE_NS.INSURANCE }),
    enableSorting: true,
  },
  {
    id: 'contracts',
    accessorKey: 'contracts',
    header: t('AdministratorsPage.Table.contracts.label', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    id: 'centers',
    accessorKey: 'centers',
    header: t('AdministratorsPage.Table.centers.label', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    id: 'role',
    accessorKey: 'role',
    header: t('AdministratorsPage.Table.role.label', { ns: LOCIZE_NS.INSURANCE }),
    meta: {
      columnFilter: {
        options: [
          {
            value: ADMIN_ROLES.ADMIN,
            label: t('AdministratorsPage.Table.role.options.ADMIN', { ns: LOCIZE_NS.INSURANCE }),
          },
          {
            value: ADMIN_ROLES.MANAGER,
            label: t('AdministratorsPage.Table.role.options.MANAGER', { ns: LOCIZE_NS.INSURANCE }),
          },
          {
            value: ADMIN_ROLES.BILLING,
            label: t('AdministratorsPage.Table.role.options.BILLING', { ns: LOCIZE_NS.INSURANCE }),
          },
        ],
      },
    },
    cell: ({ row }): React.ReactElement => (
      <Text tag="span" variant="text-sm-regular" className="text-denim-800">
        <Trans
          ns={LOCIZE_NS.INSURANCE}
          i18nKey={`AdministratorsPage.Table.role.value.${row.original.role}`}
          components={{
            description: <Text variant="text-xs-regular" className="text-gray-500" />,
          }}
        />
      </Text>
    ),
  },
  {
    id: 'email',
    accessorKey: 'email',
    header: (): string => t('AdministratorsPage.Table.email.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): React.ReactElement => (
      <Text variant="text-xs-regular" className="text-denim-900">
        {row.original.email}
      </Text>
    ),
  },
  {
    id: 'actions',
    cell: ({ row }): React.ReactElement => <AdministratorActions admin={row.original} />,
  },
];

export const Administrators = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const columns = getColumns();
  const { table, query, totalCount } = useFetchAdmins(columns);
  const { showDialog } = useDialogContext();
  const showLoading = useDelayedLoading(query.isLoading);

  const counts = useMemo(
    () => ({
      total: totalCount,
      admin: query.data?.filter(admin => admin.role === ADMIN_ROLES.ADMIN).length ?? 0,
      manager: query.data?.filter(admin => admin.role === ADMIN_ROLES.MANAGER).length ?? 0,
      billing: query.data?.filter(admin => admin.role === ADMIN_ROLES.BILLING).length ?? 0,
    }),
    [query.data, totalCount]
  );

  if (!table) {
    return null;
  }

  const description = t('AdministratorsPage.description', {
    total: counts.total,
    admin: counts.admin,
    manager: counts.manager,
    billing: counts.billing,
  });

  return (
    <div className="py-4">
      <PageHeader title={t('AdministratorsPage.heading')} description={description}>
        <Button
          type="button"
          variant="secondary"
          size="md"
          icon="leading"
          onClick={() => showDialog({ component: <AddAdministrator /> })}
        >
          <UserCog />
          {t('InsuranceAdministrators.addAdmin.cta')}
        </Button>
      </PageHeader>
      <PaginatedTable<AdminEnterprise>
        table={table}
        columns={columns}
        isLoading={showLoading}
        emptyTitle={t('InsuredPersonsList.empty')}
        withSearch
      />
    </div>
  );
};
